/**
 * NotificationsTab - Settings tab for webhook and Slack notifications
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  Plus,
  Trash2,
  TestTube,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Webhook,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';

interface Webhook {
  id: string;
  name: string;
  url: string;
  event_types: string[];
  headers: Record<string, string>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  last_triggered_at?: string;
  failure_count: number;
  last_error?: string;
}

interface SlackIntegration {
  id: string;
  webhook_url_configured: boolean;
  signing_secret_configured: boolean;
  channel_id?: string;
  channel_name?: string;
  notification_types: string[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface EventType {
  event_type: string;
  description: string;
  category: string;
}

export function NotificationsTab() {
  const { toast } = useToast();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [slackIntegration, setSlackIntegration] = useState<SlackIntegration | null>(null);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add webhook form state
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    event_types: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newWebhookSecret, setNewWebhookSecret] = useState<string | null>(null);

  // Slack form state
  const [showSlackSetup, setShowSlackSetup] = useState(false);
  const [slackForm, setSlackForm] = useState({
    webhook_url: '',
    signing_secret: '',
    channel_name: '',
    notification_types: [] as string[],
  });
  const [showSlackSecret, setShowSlackSecret] = useState(false);

  // Testing state
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null);
  const [testingSlack, setTestingSlack] = useState(false);

  const fetchEventTypes = useCallback(async () => {
    try {
      const response = await api.get('/webhooks/event-types');
      setEventTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  }, []);

  const fetchWebhooks = useCallback(async () => {
    try {
      const response = await api.get('/webhooks');
      setWebhooks(response.data);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    }
  }, []);

  const fetchSlackIntegration = useCallback(async () => {
    try {
      const response = await api.get('/integrations/slack');
      setSlackIntegration(response.data);
    } catch (error) {
      console.error('Failed to fetch Slack integration:', error);
      setSlackIntegration(null);
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchEventTypes(), fetchWebhooks(), fetchSlackIntegration()]);
    setIsLoading(false);
  }, [fetchEventTypes, fetchWebhooks, fetchSlackIntegration]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddWebhook = async () => {
    if (!newWebhook.name || !newWebhook.url) {
      toast({
        title: 'Validation Error',
        description: 'Name and URL are required',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post('/webhooks', newWebhook);
      setNewWebhookSecret(response.data.secret);
      toast({
        title: 'Webhook Created',
        description: 'Store the secret securely - it won\'t be shown again.',
      });
      fetchWebhooks();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create webhook',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteWebhook = async (id: string) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;

    try {
      await api.delete(`/webhooks/${id}`);
      toast({ title: 'Deleted', description: 'Webhook deleted successfully' });
      fetchWebhooks();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete webhook',
        variant: 'destructive',
      });
    }
  };

  const handleTestWebhook = async (id: string) => {
    setTestingWebhookId(id);
    try {
      const response = await api.post(`/webhooks/${id}/test`);
      if (response.data.success) {
        toast({
          title: 'Test Successful',
          description: `Webhook responded in ${response.data.response_time_ms}ms`,
        });
      } else {
        toast({
          title: 'Test Failed',
          description: response.data.error || 'Webhook test failed',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to test webhook',
        variant: 'destructive',
      });
    } finally {
      setTestingWebhookId(null);
    }
  };

  const handleToggleWebhook = async (id: string, enabled: boolean) => {
    try {
      await api.patch(`/webhooks/${id}`, { enabled });
      fetchWebhooks();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update webhook',
        variant: 'destructive',
      });
    }
  };

  const handleSetupSlack = async () => {
    if (!slackForm.webhook_url) {
      toast({
        title: 'Validation Error',
        description: 'Webhook URL is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/integrations/slack', slackForm);
      toast({ title: 'Success', description: 'Slack integration configured' });
      setShowSlackSetup(false);
      setSlackForm({
        webhook_url: '',
        signing_secret: '',
        channel_name: '',
        notification_types: [],
      });
      fetchSlackIntegration();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to setup Slack',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTestSlack = async () => {
    setTestingSlack(true);
    try {
      const response = await api.post('/integrations/slack/test');
      if (response.data.success) {
        toast({ title: 'Test Successful', description: 'Message sent to Slack' });
      } else {
        toast({
          title: 'Test Failed',
          description: response.data.error || 'Slack test failed',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to test Slack',
        variant: 'destructive',
      });
    } finally {
      setTestingSlack(false);
    }
  };

  const handleDeleteSlack = async () => {
    if (!confirm('Are you sure you want to remove Slack integration?')) return;

    try {
      await api.delete('/integrations/slack');
      toast({ title: 'Deleted', description: 'Slack integration removed' });
      setSlackIntegration(null);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to remove Slack',
        variant: 'destructive',
      });
    }
  };

  const toggleEventType = (eventType: string, list: string[], setList: (v: string[]) => void) => {
    if (list.includes(eventType)) {
      setList(list.filter((t) => t !== eventType));
    } else {
      setList([...list, eventType]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Webhooks Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Webhooks
              </CardTitle>
              <CardDescription>
                Send notifications to external services when events occur
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowAddWebhook(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Webhook
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {webhooks.length === 0 && !showAddWebhook ? (
            <p className="text-center text-muted-foreground py-4">
              No webhooks configured
            </p>
          ) : (
            <div className="space-y-4">
              {webhooks.map((webhook) => (
                <div
                  key={webhook.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{webhook.name}</span>
                      {webhook.enabled ? (
                        <Badge variant="outline" className="text-green-500">
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Disabled</Badge>
                      )}
                      {webhook.failure_count > 0 && (
                        <Badge variant="destructive">
                          {webhook.failure_count} failures
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate max-w-md">
                      {webhook.url}
                    </div>
                    {webhook.event_types.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {webhook.event_types.slice(0, 3).map((et) => (
                          <Badge key={et} variant="outline" className="text-xs">
                            {et}
                          </Badge>
                        ))}
                        {webhook.event_types.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{webhook.event_types.length - 3} more
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={webhook.enabled}
                      onCheckedChange={(checked) =>
                        handleToggleWebhook(webhook.id, checked)
                      }
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestWebhook(webhook.id)}
                      disabled={testingWebhookId === webhook.id}
                    >
                      {testingWebhookId === webhook.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Webhook Form */}
          {showAddWebhook && (
            <div className="mt-4 p-4 border rounded-lg space-y-4">
              {newWebhookSecret ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Webhook Created!</span>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <Label className="text-xs text-muted-foreground">
                      Webhook Secret (save this - it won't be shown again)
                    </Label>
                    <code className="block mt-1 text-sm break-all">
                      {newWebhookSecret}
                    </code>
                  </div>
                  <Button
                    onClick={() => {
                      setShowAddWebhook(false);
                      setNewWebhook({ name: '', url: '', event_types: [] });
                      setNewWebhookSecret(null);
                    }}
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        placeholder="My Webhook"
                        value={newWebhook.name}
                        onChange={(e) =>
                          setNewWebhook({ ...newWebhook, name: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL</Label>
                      <Input
                        placeholder="https://..."
                        value={newWebhook.url}
                        onChange={(e) =>
                          setNewWebhook({ ...newWebhook, url: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Event Types (empty = all events)</Label>
                    <div className="max-h-64 overflow-y-auto p-2 border rounded space-y-4">
                      {Object.entries(
                        eventTypes.reduce((acc, et) => {
                          if (!acc[et.category]) acc[et.category] = [];
                          acc[et.category].push(et);
                          return acc;
                        }, {} as Record<string, EventType[]>)
                      ).map(([category, types]) => (
                        <div key={category}>
                          <div className="text-xs font-semibold text-muted-foreground mb-2">
                            {category}
                          </div>
                          <div className="space-y-2">
                            {types.map((et) => (
                              <div key={et.event_type} className="flex items-start gap-2">
                                <Checkbox
                                  id={`webhook-${et.event_type}`}
                                  checked={newWebhook.event_types.includes(et.event_type)}
                                  onCheckedChange={() =>
                                    toggleEventType(
                                      et.event_type,
                                      newWebhook.event_types,
                                      (v) =>
                                        setNewWebhook({ ...newWebhook, event_types: v })
                                    )
                                  }
                                  className="mt-0.5"
                                />
                                <Label
                                  htmlFor={`webhook-${et.event_type}`}
                                  className="text-sm cursor-pointer leading-tight"
                                >
                                  <span className="font-medium">{et.event_type}</span>
                                  <span className="block text-xs text-muted-foreground">
                                    {et.description}
                                  </span>
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddWebhook(false);
                        setNewWebhook({ name: '', url: '', event_types: [] });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleAddWebhook} disabled={isSubmitting}>
                      {isSubmitting && (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      )}
                      Create Webhook
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Slack Integration Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Slack Integration
              </CardTitle>
              <CardDescription>
                Receive notifications in Slack with interactive response buttons
              </CardDescription>
            </div>
            {!slackIntegration && (
              <Button size="sm" onClick={() => setShowSlackSetup(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Setup Slack
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {slackIntegration ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Slack Integration</span>
                    {slackIntegration.enabled ? (
                      <Badge variant="outline" className="text-green-500">
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Disabled</Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {slackIntegration.channel_name
                      ? `Channel: #${slackIntegration.channel_name}`
                      : 'Channel configured via webhook'}
                  </div>
                  {slackIntegration.notification_types.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {slackIntegration.notification_types.slice(0, 3).map((nt) => (
                        <Badge key={nt} variant="outline" className="text-xs">
                          {nt}
                        </Badge>
                      ))}
                      {slackIntegration.notification_types.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{slackIntegration.notification_types.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleTestSlack}
                    disabled={testingSlack}
                  >
                    {testingSlack ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <TestTube className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteSlack}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="text-sm text-muted-foreground p-3 bg-muted rounded-lg">
                <p className="font-medium mb-1">Interactive Responses</p>
                <p>
                  To enable responding to HITL requests directly from Slack, add this
                  Interactivity Request URL to your Slack App settings:
                </p>
                <code className="block mt-2 p-2 bg-background rounded text-xs">
                  {window.location.origin}/api/integrations/slack/interact
                </code>
              </div>
            </div>
          ) : showSlackSetup ? (
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="space-y-2">
                <Label>Incoming Webhook URL</Label>
                <Input
                  placeholder="https://hooks.slack.com/services/..."
                  value={slackForm.webhook_url}
                  onChange={(e) =>
                    setSlackForm({ ...slackForm, webhook_url: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Create an incoming webhook in your Slack workspace settings
                </p>
              </div>

              <div className="space-y-2">
                <Label>Signing Secret (for interactive responses)</Label>
                <div className="relative">
                  <Input
                    type={showSlackSecret ? 'text' : 'password'}
                    placeholder="Your Slack App signing secret"
                    value={slackForm.signing_secret}
                    onChange={(e) =>
                      setSlackForm({ ...slackForm, signing_secret: e.target.value })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowSlackSecret(!showSlackSecret)}
                  >
                    {showSlackSecret ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Optional - required for approving HITL requests from Slack
                </p>
              </div>

              <div className="space-y-2">
                <Label>Channel Name (optional)</Label>
                <Input
                  placeholder="general"
                  value={slackForm.channel_name}
                  onChange={(e) =>
                    setSlackForm({ ...slackForm, channel_name: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Notification Types (empty = all)</Label>
                <div className="max-h-64 overflow-y-auto p-2 border rounded space-y-4">
                  {Object.entries(
                    eventTypes.reduce((acc, et) => {
                      if (!acc[et.category]) acc[et.category] = [];
                      acc[et.category].push(et);
                      return acc;
                    }, {} as Record<string, EventType[]>)
                  ).map(([category, types]) => (
                    <div key={category}>
                      <div className="text-xs font-semibold text-muted-foreground mb-2">
                        {category}
                      </div>
                      <div className="space-y-2">
                        {types.map((et) => (
                          <div key={et.event_type} className="flex items-start gap-2">
                            <Checkbox
                              id={`slack-${et.event_type}`}
                              checked={slackForm.notification_types.includes(et.event_type)}
                              onCheckedChange={() =>
                                toggleEventType(
                                  et.event_type,
                                  slackForm.notification_types,
                                  (v) =>
                                    setSlackForm({ ...slackForm, notification_types: v })
                                )
                              }
                              className="mt-0.5"
                            />
                            <Label
                              htmlFor={`slack-${et.event_type}`}
                              className="text-sm cursor-pointer leading-tight"
                            >
                              <span className="font-medium">{et.event_type}</span>
                              <span className="block text-xs text-muted-foreground">
                                {et.description}
                              </span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowSlackSetup(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSetupSlack} disabled={isSubmitting}>
                  {isSubmitting && (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  )}
                  Setup Slack
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              Slack integration not configured
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default NotificationsTab;
